import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  TextField,
  TableSortLabel,
  TableContainer,
  Button,
  Stack,
  InputLabel,
  Alert,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api';
import { useUser } from '../UserContext';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip, PieChart, Pie, Cell, Legend } from 'recharts';

function InventoryItemsAnalytics() {
  const { data = [], isLoading, isError } = useQuery({
    queryKey: ['analytics', 'inventoryItems'],
    queryFn: async () => {
      const response = await api.get('/analytics/inventory-items');
      return response.data;
    }
  });

  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState('amountSum');
  const [order, setOrder] = useState('desc');

  // Filter and sort data
  const filtered = useMemo(() => {
    let rows = data;
    if (search) {
      rows = rows.filter(row => (row.title || '').toLowerCase().includes(search.toLowerCase()));
    }
    rows = [...rows].sort((a, b) => {
      let aVal = a[orderBy] ?? '';
      let bVal = b[orderBy] ?? '';
      if (orderBy === 'title') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      } else {
        aVal = Number(aVal);
        bVal = Number(bVal);
      }
      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
    return rows;
  }, [data, search, orderBy, order]);

  const handleSort = (col) => {
    if (orderBy === col) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(col);
      setOrder('asc');
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
        </Stack>
      </Box>
    );
  }
  if (isError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load analytics.
      </Alert>
    );
  }

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="h6" gutterBottom>Inventory Items Summary</Typography>
      <TextField
        label="Search by Title"
        value={search}
        onChange={e => setSearch(e.target.value)}
        size="small"
        sx={{ mb: 2 }}
      />
      <TableContainer>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === 'title' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'title'}
                  direction={orderBy === 'title' ? order : 'asc'}
                  onClick={() => handleSort('title')}
                >
                  Asset Title
                </TableSortLabel>
              </TableCell>
              <TableCell>Asset ID</TableCell>
              <TableCell align="right" sortDirection={orderBy === 'amountSum' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'amountSum'}
                  direction={orderBy === 'amountSum' ? order : 'desc'}
                  onClick={() => handleSort('amountSum')}
                >
                  Total Amount
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((row) => (
              <TableRow key={row.assetId}>
                <TableCell>{row.title || 'Unknown'}</TableCell>
                <TableCell>{row.assetId}</TableCell>
                <TableCell align="right">{row.amountSum}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

function AuctionAnalytics() {
  const [from, setFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(() => (new Date()).toISOString().slice(0, 10));
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['analytics', 'auction-items', from, to],
    queryFn: async () => {
      const response = await api.get('/analytics/auction-items', { params: { from, to } });
      return response.data;
    }
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
        </Stack>
      </Box>
    );
  }
  if (isError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load auction analytics.
      </Alert>
    );
  }

  const { summary, topAssets, timeSeries } = data || {};
  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="h6" gutterBottom>Auction Analytics</Typography>
      <Stack direction="row" spacing={2} mb={2} alignItems="center">
        <InputLabel>Date Range:</InputLabel>
        <TextField type="date" size="small" value={from} onChange={e => setFrom(e.target.value)} />
        <TextField type="date" size="small" value={to} onChange={e => setTo(e.target.value)} />
        <Button variant="contained" onClick={() => refetch()}>Apply</Button>
      </Stack>
      <Stack spacing={1} mb={2}>
        <Typography>Total Moani Spent: <b>{Number(summary?.totalMoani || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</b></Typography>
        <Typography>Total Items Sold: <b>{summary?.totalItems || 0}</b></Typography>
      </Stack>
      <Typography variant="subtitle1" mt={2}>Top Selling Assets</Typography>
      <TableContainer sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Asset</TableCell>
              <TableCell>Items Sold</TableCell>
              <TableCell>Moani Sold</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(topAssets || []).map(row => (
              <TableRow key={row.assetId}>
                <TableCell>{row.title || row.assetId}</TableCell>
                <TableCell>{row.itemsSold}</TableCell>
                <TableCell>{Number(row.moaniSold).toLocaleString(undefined, { maximumFractionDigits: 2 })}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="subtitle1" mt={2}>Sales Over Time</Typography>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={timeSeries || []}>
          <XAxis dataKey="date" />
          <YAxis yAxisId="left" label={{ value: 'Moani', angle: -90, position: 'insideLeft' }} />
          <YAxis yAxisId="right" orientation="right" label={{ value: 'Items', angle: 90, position: 'insideRight' }} />
          <RechartsTooltip />
          <Legend />
          <Line yAxisId="left" type="monotone" dataKey="moaniSold" stroke="#8884d8" name="Moani Sold" />
          <Line yAxisId="right" type="monotone" dataKey="itemsSold" stroke="#82ca9d" name="Items Sold" />
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );
}

function MoaniAnalytics() {
  const { data: balances, isLoading: loadingBalances } = useQuery({
    queryKey: ['analytics', 'moani-balances'],
    queryFn: async () => {
      const response = await api.get('/analytics/moani-balances');
      return response.data;
    }
  });
  // Selected dates (what user sees in inputs)
  const [selectedFrom, setSelectedFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().slice(0, 10); });
  const [selectedTo, setSelectedTo] = useState(() => (new Date()).toISOString().slice(0, 10));
  // Applied dates (what gets sent to API)
  const [appliedFrom, setAppliedFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().slice(0, 10); });
  const [appliedTo, setAppliedTo] = useState(() => (new Date()).toISOString().slice(0, 10));
  
  const { data: splits, isLoading: loadingSplits } = useQuery({
    queryKey: ['analytics', 'moani-splits', appliedFrom, appliedTo],
    queryFn: async () => {
      const response = await api.get('/analytics/moani-splits', { params: { from: appliedFrom, to: appliedTo } });
      return response.data;
    }
  });
  const { data: timeSeriesResponse, isLoading: loadingTimeSeries } = useQuery({
    queryKey: ['analytics', 'moani-splits-timeseries', appliedFrom, appliedTo],
    queryFn: async () => {
      const response = await api.get('/analytics/moani-splits-timeseries', { params: { from: appliedFrom, to: appliedTo } });
      return response.data;
    }
  });
  
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD'];
  
  // Group for pie chart: context+type
  let pieData = [];
  if (splits && splits.length) {
    const sorted = [...splits].sort((a, b) => b.moaniSum - a.moaniSum);
    const top = sorted.slice(0, 6);
    const restSum = sorted.slice(6).reduce((acc, cur) => acc + Number(cur.moaniSum), 0);
    pieData = top.map(row => ({
      ...row,
      label: `${row.context || 'Unknown'}:${row.type}`
    }));
    if (restSum > 0) pieData.push({ label: 'Other', moaniSum: restSum });
  }

  // Process time series data
  const timeSeries = timeSeriesResponse?.data || [];
  const categories = timeSeriesResponse?.categories || [];
  
  // Get top categories for the line chart (limit to top 6 to avoid clutter)
  const topCategories = categories
    .map(category => ({
      category,
      total: timeSeries.reduce((sum, entry) => sum + (entry[category] || 0), 0)
    }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 6)
    .map(item => item.category);

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="h6" gutterBottom>Moani Analytics</Typography>
      <Box mb={2}>
        {loadingBalances ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={20} />
            <Typography>Loading balances...</Typography>
          </Stack>
        ) : (
          <Typography>Total Moani in User Accounts: <b>{Number(balances?.totalMoani || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</b></Typography>
        )}
      </Box>
      <Stack direction="row" spacing={2} mb={2} alignItems="center">
        <InputLabel>Date Range:</InputLabel>
        <TextField type="date" size="small" value={selectedFrom} onChange={e => setSelectedFrom(e.target.value)} />
        <TextField type="date" size="small" value={selectedTo} onChange={e => setSelectedTo(e.target.value)} />
        <Button variant="contained" onClick={() => {
          setAppliedFrom(selectedFrom);
          setAppliedTo(selectedTo);
        }}>Apply</Button>
      </Stack>

      {/* Category Trends Over Time */}
      <Typography variant="subtitle1" mt={3} mb={2}>Transaction Categories Over Time</Typography>
      {loadingTimeSeries ? (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ height: 300 }}>
          <CircularProgress size={20} />
          <Typography>Loading trends...</Typography>
        </Stack>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={timeSeries}>
            <XAxis 
              dataKey="date" 
              tickFormatter={(value) => {
                const date = new Date(value);
                return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
              }}
            />
            <YAxis 
              label={{ value: 'Moani Amount', angle: -90, position: 'insideLeft' }}
              tickFormatter={(value) => value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            />
            <RechartsTooltip 
              formatter={(value, name) => [
                Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 }),
                name
              ]}
              labelFormatter={(label) => {
                const date = new Date(label);
                return date.toLocaleDateString(undefined, { 
                  weekday: 'short', 
                  year: 'numeric', 
                  month: 'short', 
                  day: 'numeric' 
                });
              }}
            />
            <Legend />
            {topCategories.map((category, index) => (
              <Line 
                key={category}
                type="monotone" 
                dataKey={category} 
                stroke={COLORS[index % COLORS.length]} 
                name={category}
                strokeWidth={2}
                dot={{ fill: COLORS[index % COLORS.length], strokeWidth: 2, r: 3 }}
                connectNulls={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}

      <Typography variant="subtitle1" mt={3} mb={2}>Moani Split by Context & Type</Typography>
      {loadingSplits ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={20} />
          <Typography>Loading splits...</Typography>
        </Stack>
      ) : (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} alignItems="center">
          <ResponsiveContainer width={400} height={400}>
            <PieChart>
              <Pie data={pieData} dataKey="moaniSum" nameKey="label" cx="50%" cy="50%" outerRadius={150} label>
                {pieData.map((entry, idx) => <Cell key={entry.label} fill={COLORS[idx % COLORS.length]} />)}
              </Pie>
              <RechartsTooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Context</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Moani</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(splits || []).map(row => (
                  <TableRow key={row.context + ':' + row.type}>
                    <TableCell>{row.context || 'Unknown'}</TableCell>
                    <TableCell>{row.type}</TableCell>
                    <TableCell>{Number(row.moaniSum).toLocaleString(undefined, { maximumFractionDigits: 2 })}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}
    </Paper>
  );
}

function MoaniOverview() {
  // Selected values (what user sees in inputs)
  const [selectedFrom, setSelectedFrom] = useState(() => {
    const d = new Date(); 
    d.setFullYear(d.getFullYear() - 1); // 12 months back
    return d.toISOString().slice(0, 10);
  });
  const [selectedTo, setSelectedTo] = useState(() => (new Date()).toISOString().slice(0, 10));
  const [selectedPeriod, setSelectedPeriod] = useState('daily');
  
  // Applied values (what gets sent to API)
  const [appliedFrom, setAppliedFrom] = useState(() => {
    const d = new Date(); 
    d.setFullYear(d.getFullYear() - 1); // 12 months back
    return d.toISOString().slice(0, 10);
  });
  const [appliedTo, setAppliedTo] = useState(() => (new Date()).toISOString().slice(0, 10));
  const [appliedPeriod, setAppliedPeriod] = useState('daily');
  
  const { data: overviewData, isLoading, isError } = useQuery({
    queryKey: ['analytics', 'moani-overview', appliedFrom, appliedTo, appliedPeriod], 
    queryFn: async () => {
      const response = await api.get('/analytics/moani-overview', { 
        params: { from: appliedFrom, to: appliedTo, period: appliedPeriod } 
      });
      return response.data;
    }
  });

  // Calculate totals
  const totals = useMemo(() => {
    if (!overviewData || !overviewData.length) return { totalSources: 0, totalSinks: 0, netFlow: 0 };
    
    const totalSources = overviewData.reduce((sum, item) => sum + Number(item.sources || 0), 0);
    const totalSinks = overviewData.reduce((sum, item) => sum + Number(item.sinks || 0), 0);
    const netFlow = totalSources - totalSinks;
    
    return { totalSources, totalSinks, netFlow };
  }, [overviewData]);

  // Calculate cumulative data
  const cumulativeData = useMemo(() => {
    if (!overviewData || !overviewData.length) return [];
    
    let cumulativeSources = 0;
    let cumulativeSinks = 0;
    
    return overviewData.map(item => {
      cumulativeSources += Number(item.sources || 0);
      cumulativeSinks += Number(item.sinks || 0);
      
      return {
        ...item,
        cumulativeSources,
        cumulativeSinks,
        cumulativeNet: cumulativeSources - cumulativeSinks
      };
    });
  }, [overviewData]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography>Loading moani overview...</Typography>
        </Stack>
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load moani overview analytics.
      </Alert>
    );
  }

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="h6" gutterBottom>Moani Overview</Typography>
      
      {/* Controls */}
      <Stack direction="row" spacing={2} mb={2} alignItems="center" flexWrap="wrap">
        <InputLabel>Date Range:</InputLabel>
        <TextField 
          type="date" 
          size="small" 
          value={selectedFrom} 
          onChange={e => setSelectedFrom(e.target.value)} 
        />
        <TextField 
          type="date" 
          size="small" 
          value={selectedTo} 
          onChange={e => setSelectedTo(e.target.value)} 
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Period</InputLabel>
          <Select
            value={selectedPeriod}
            label="Period"
            onChange={e => setSelectedPeriod(e.target.value)}
          >
            <MenuItem value="daily">Daily</MenuItem>
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" onClick={() => {
          setAppliedFrom(selectedFrom);
          setAppliedTo(selectedTo);
          setAppliedPeriod(selectedPeriod);
        }}>Apply</Button>
      </Stack>

      {/* Summary Stats */}
      <Stack direction="row" spacing={4} mb={3} flexWrap="wrap">
        <Box>
          <Typography variant="subtitle2" color="text.secondary">Total Sources</Typography>
          <Typography variant="h6" color="success.main">
            +{totals.totalSources.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">Total Sinks</Typography>
          <Typography variant="h6" color="error.main">
            -{totals.totalSinks.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">Net Flow</Typography>
          <Typography 
            variant="h6" 
            color={totals.netFlow >= 0 ? "success.main" : "error.main"}
          >
            {totals.netFlow >= 0 ? '+' : ''}{totals.netFlow.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </Typography>
        </Box>
      </Stack>

      {/* Daily Sources vs Sinks Chart */}
      <Typography variant="subtitle1" mb={2}>Daily Sources vs Sinks</Typography>
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={overviewData || []}>
          <XAxis 
            dataKey="date" 
            tickFormatter={(value) => {
              const date = new Date(value);
              if (appliedPeriod === 'monthly') {
                return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
              } else if (appliedPeriod === 'weekly') {
                return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
              }
              return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
            }}
          />
          <YAxis 
            label={{ value: 'Moani Amount', angle: -90, position: 'insideLeft' }}
            tickFormatter={(value) => value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          />
          <RechartsTooltip 
            formatter={(value, name) => [
              Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 }),
              name
            ]}
            labelFormatter={(label) => {
              const date = new Date(label);
              return date.toLocaleDateString(undefined, { 
                weekday: 'short', 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric' 
              });
            }}
          />
          <Legend />
          <Line 
            type="monotone" 
            dataKey="sources" 
            stroke="#4caf50" 
            name="Sources" 
            strokeWidth={2}
            dot={{ fill: '#4caf50', strokeWidth: 2, r: 4 }}
          />
          <Line 
            type="monotone" 
            dataKey="sinks" 
            stroke="#f44336" 
            name="Sinks" 
            strokeWidth={2}
            dot={{ fill: '#f44336', strokeWidth: 2, r: 4 }}
          />
        </LineChart>
      </ResponsiveContainer>

      {/* Cumulative Sources vs Sinks Chart */}
      <Typography variant="subtitle1" mt={4} mb={2}>Cumulative Sources vs Sinks</Typography>
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={cumulativeData}>
          <XAxis 
            dataKey="date" 
            tickFormatter={(value) => {
              const date = new Date(value);
              if (appliedPeriod === 'monthly') {
                return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
              } else if (appliedPeriod === 'weekly') {
                return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
              }
              return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
            }}
          />
          <YAxis 
            label={{ value: 'Cumulative Moani', angle: -90, position: 'insideLeft' }}
            tickFormatter={(value) => value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          />
          <RechartsTooltip 
            formatter={(value, name) => [
              Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 }),
              name
            ]}
            labelFormatter={(label) => {
              const date = new Date(label);
              return date.toLocaleDateString(undefined, { 
                weekday: 'short', 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric' 
              });
            }}
          />
          <Legend />
          <Line 
            type="monotone" 
            dataKey="cumulativeSources" 
            stroke="#4caf50" 
            name="Cumulative Sources" 
            strokeWidth={3}
            dot={{ fill: '#4caf50', strokeWidth: 2, r: 4 }}
          />
          <Line 
            type="monotone" 
            dataKey="cumulativeSinks" 
            stroke="#f44336" 
            name="Cumulative Sinks" 
            strokeWidth={3}
            dot={{ fill: '#f44336', strokeWidth: 2, r: 4 }}
          />
          <Line 
            type="monotone" 
            dataKey="cumulativeNet" 
            stroke="#2196f3" 
            name="Cumulative Net Flow" 
            strokeWidth={3}
            strokeDasharray="5 5"
            dot={{ fill: '#2196f3', strokeWidth: 2, r: 4 }}
          />
        </LineChart>
      </ResponsiveContainer>

      {/* Data Table */}
      <Typography variant="subtitle1" mt={4} mb={2}>
        {appliedPeriod.charAt(0).toUpperCase() + appliedPeriod.slice(1)} Data Table
      </Typography>
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>
                  {appliedPeriod === 'daily' ? 'Date' : appliedPeriod === 'weekly' ? 'Week' : 'Month'}
                </strong>
              </TableCell>
              <TableCell align="right">
                <strong style={{ color: '#4caf50' }}>Sources</strong>
              </TableCell>
              <TableCell align="right">
                <strong style={{ color: '#f44336' }}>Sinks</strong>
              </TableCell>
              <TableCell align="right">
                <strong style={{ color: '#2196f3' }}>Net Flow</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(overviewData || []).map((row, index) => {
              const sources = Number(row.sources || 0);
              const sinks = Number(row.sinks || 0);
              const netFlow = sources - sinks;
              
              return (
                <TableRow key={index} hover>
                  <TableCell>
                    {(() => {
                      const date = new Date(row.date);
                      if (appliedPeriod === 'monthly') {
                        return date.toLocaleDateString(undefined, { 
                          month: 'long', 
                          year: 'numeric' 
                        });
                      } else if (appliedPeriod === 'weekly') {
                        return `Week of ${date.toLocaleDateString(undefined, { 
                          month: 'short', 
                          day: 'numeric',
                          year: 'numeric'
                        })}`;
                      }
                      return date.toLocaleDateString(undefined, { 
                        weekday: 'short',
                        month: 'short', 
                        day: 'numeric',
                        year: 'numeric'
                      });
                    })()}
                  </TableCell>
                  <TableCell align="right" style={{ color: sources > 0 ? '#4caf50' : 'inherit' }}>
                    +{sources.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell align="right" style={{ color: sinks > 0 ? '#f44336' : 'inherit' }}>
                    -{sinks.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell 
                    align="right" 
                    style={{ 
                      color: netFlow >= 0 ? '#4caf50' : '#f44336',
                      fontWeight: 'bold'
                    }}
                  >
                    {netFlow >= 0 ? '+' : ''}{netFlow.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

function MoaniLeaderboard() {
  const navigate = useNavigate();
  const { selectUserById } = useUser();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(1000);
  const [refreshingUsers, setRefreshingUsers] = useState(new Set());

  const { data, isLoading, isError } = useQuery({
    queryKey: ['analytics', 'moani-leaderboard', page, pageSize], 
    queryFn: async () => {
      const response = await api.get('/analytics/moani-leaderboard', {
        params: { page, limit: pageSize }
      });
      return response.data;
    }
  });

  // Mutation for refreshing NFT collection
  const refreshNFTMutation = useMutation({
    mutationFn: async (userId) => {
      const response = await api.post(`/users/${userId}/refresh-nft-collection`);
      return response.data;
    },
    onSuccess: (data, userId) => {
      // Show success message
      alert(`NFT collection refreshed successfully for user ${userId}. Previous: ${data.previousTokenCount}, New: ${data.newTokenCount}`);
      // Remove from refreshing set
      setRefreshingUsers(prev => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
      // Optionally refetch leaderboard to show updated data
      queryClient.invalidateQueries(['analytics', 'moani-leaderboard']);
    },
    onError: (error, userId) => {
      alert(`Failed to refresh NFT collection: ${error.response?.data?.error || error.message}`);
      // Remove from refreshing set
      setRefreshingUsers(prev => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    },
    onMutate: (userId) => {
      // Add to refreshing set
      setRefreshingUsers(prev => new Set(prev).add(userId));
    }
  });

  const handleRefreshNFT = (userId, event) => {
    event.stopPropagation(); // Prevent row click
    if (window.confirm(`Refresh NFT collection for this user?`)) {
      refreshNFTMutation.mutate(userId);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography>Loading moani leaderboard...</Typography>
        </Stack>
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load moani leaderboard.
      </Alert>
    );
  }

  const { leaderboard = [], summary = {}, pagination = {} } = data || {};


  return (
    <Box sx={{ p: 3, mt: 2 }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
        🏆 Moani Leaderboard
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, pagination.totalCount || 0)} of {pagination.totalCount || 0} users
      </Typography>
      
      {/* Pagination Controls */}
      <Box sx={{ p: 2, mb: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Page Size</InputLabel>
            <Select
              value={pageSize}
              label="Page Size"
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              <MenuItem value={500}>500</MenuItem>
              <MenuItem value={1000}>1000</MenuItem>
              <MenuItem value={2000}>2000</MenuItem>
              <MenuItem value={5000}>5000</MenuItem>
            </Select>
          </FormControl>
          
          <Button 
            variant="outlined" 
            disabled={!pagination.hasPreviousPage}
            onClick={() => setPage(page - 1)}
            sx={{ minWidth: 80 }}
          >
            Previous
          </Button>
          
          <Box sx={{ 
            px: 2, 
            py: 1, 
            bgcolor: 'primary.main', 
            color: 'white', 
            borderRadius: 1,
            fontWeight: 'bold'
          }}>
            Page {pagination.currentPage || 1} of {pagination.totalPages || 1}
          </Box>
          
          <Button 
            variant="outlined" 
            disabled={!pagination.hasNextPage}
            onClick={() => setPage(page + 1)}
            sx={{ minWidth: 80 }}
          >
            Next
          </Button>
          
          <TextField
            size="small"
            label="Go to page"
            type="number"
            value={page}
            onChange={(e) => {
              const newPage = Math.max(1, Math.min(Number(e.target.value) || 1, pagination.totalPages || 1));
              setPage(newPage);
            }}
            sx={{ width: 120 }}
            inputProps={{ min: 1, max: pagination.totalPages || 1 }}
          />
        </Stack>
      </Box>
      
      {/* Summary Statistics */}
      <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
          📊 Overall Statistics ({summary.totalUsers?.toLocaleString() || 0} users with moani)
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Box sx={{ textAlign: 'center', p: 1.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Total Users
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {summary.totalUsers?.toLocaleString() || 0}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Box sx={{ textAlign: 'center', p: 1.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Total Moani
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                {Number(summary.totalMoani || 0).toLocaleString(undefined, { 
                  maximumFractionDigits: 0 
                })}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Box sx={{ textAlign: 'center', p: 1.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Overall Average
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                {Number(summary.averageOverallMoani || 0).toLocaleString(undefined, { 
                  maximumFractionDigits: 0 
                })}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Box sx={{ textAlign: 'center', p: 1.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Page Average
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                {Number(summary.averageMoani || 0).toLocaleString(undefined, { 
                  maximumFractionDigits: 0 
                })}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Box sx={{ textAlign: 'center', p: 1.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Top Holder
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#FFD700' }}>
                {Number(summary.maxMoani || 0).toLocaleString(undefined, { 
                  maximumFractionDigits: 0 
                })}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Leaderboard Table */}
      <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Table sx={{ '& .MuiTableCell-root': { py: 2 } }}>
          <TableHead sx={{ bgcolor: 'primary.main' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.9rem' }}>Rank</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.9rem' }}>User</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.9rem' }}>Status</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.9rem' }}>Email</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.9rem' }}>Wallet</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.9rem' }}>Moani Balance</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.9rem' }}>Account Created</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.9rem' }}>Last Online</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.9rem' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
                     <TableBody>
             {leaderboard.map((entry, index) => {
               const globalRank = (page - 1) * pageSize + index + 1;
               return (
               <TableRow 
                 key={entry.userId || index} 
                 hover
                 sx={{ 
                   '&:nth-of-type(odd)': { bgcolor: 'background.default' },
                   '&:hover': { bgcolor: 'action.hover' },
                   cursor: 'pointer'
                 }}
               >
                 <TableCell sx={{ fontWeight: 'bold', minWidth: 80 }}>
                   <Box display="flex" alignItems="center">
                     <Typography 
                       variant="body1" 
                       fontWeight="bold"
                       sx={{
                         color: globalRank === 1 ? '#FFD700' : 
                                globalRank === 2 ? '#C0C0C0' : 
                                globalRank === 3 ? '#CD7F32' : 
                                globalRank <= 10 ? 'primary.main' : 'text.primary',
                         minWidth: '40px',
                         fontSize: globalRank <= 3 ? '1.1rem' : '1rem'
                       }}
                     >
                       #{globalRank}
                     </Typography>
                     {globalRank <= 3 && (
                       <Box 
                         sx={{ 
                           ml: 1, 
                           fontSize: '18px',
                           filter: globalRank === 1 ? 'sepia(1) saturate(2) hue-rotate(38deg)' : 
                                   globalRank === 2 ? 'sepia(1) saturate(0) brightness(1.2)' : 
                                   'sepia(1) saturate(2) hue-rotate(20deg)'
                         }}
                       >
                         🏆
                       </Box>
                     )}
                   </Box>
                 </TableCell>
                                 <TableCell sx={{ minWidth: 160 }}>
                   <Box>
                    <Typography 
                      component={entry.userId ? 'a' : 'span'}
                      href={entry.userId ? `/user/${entry.userId}/details` : undefined}
                      variant="body1" 
                      fontWeight="600"
                      sx={{ 
                        cursor: entry.userId ? 'pointer' : 'default',
                        color: entry.userId ? 'primary.main' : 'text.primary',
                        textDecoration: 'none',
                        '&:hover': entry.userId ? {
                          color: 'primary.dark',
                          textDecoration: 'underline'
                        } : {},
                        fontSize: '0.95rem'
                      }}
                      onClick={() => entry.userId && selectUserById(entry.userId, navigate)}
                      title={entry.userId ? 'Click to select this user (Ctrl+click to open in new tab)' : 'User ID not available'}
                    >
                      {entry.userName || 'Unknown User'}
                    </Typography>
                     {entry.playerid && (
                       <Typography 
                         variant="caption" 
                         color="text.secondary" 
                         sx={{ 
                           display: 'block', 
                           fontSize: '0.7rem',
                           fontFamily: 'monospace',
                           opacity: 0.8
                         }}
                       >
                         ID: {entry.playerid}
                       </Typography>
                     )}
                   </Box>
                 </TableCell>
                 <TableCell>
                   <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ minWidth: 120 }}>
                     {entry.banned ? (
                       <Chip 
                         label="BANNED" 
                         color="error" 
                         size="small" 
                         sx={{ fontSize: '0.7rem', height: 20, fontWeight: 'bold' }} 
                       />
                     ) : (
                       <Chip 
                         label={(() => {
                           // Clean the status by removing any trailing numbers or whitespace
                           const cleanStatus = (entry.status || 'ACTIVE').toString().replace(/\s*\d+\s*$/, '').trim().toUpperCase();
                           return cleanStatus || 'ACTIVE';
                         })()} 
                         color={(() => {
                           const cleanStatus = (entry.status || 'ACTIVE').toString().replace(/\s*\d+\s*$/, '').trim().toUpperCase();
                           return cleanStatus === 'ACTIVE' ? 'success' : cleanStatus === 'DISABLED' ? 'warning' : 'error';
                         })()} 
                         size="small" 
                         sx={{ fontSize: '0.7rem', height: 20, fontWeight: 'bold' }} 
                       />
                     )}
                     {entry.cheater && (
                       <Chip 
                         label="CHEATER" 
                         color="warning" 
                         size="small" 
                         sx={{ fontSize: '0.7rem', height: 20, fontWeight: 'bold' }} 
                       />
                     )}
                   </Stack>
                 </TableCell>
                 <TableCell sx={{ minWidth: 200 }}>
                   <Typography 
                     variant="body2" 
                     sx={{ 
                       fontSize: '0.9rem',
                       color: entry.userEmail ? 'text.primary' : 'text.secondary',
                       fontStyle: entry.userEmail ? 'normal' : 'italic'
                     }}
                   >
                     {entry.userEmail || 'No email'}
                   </Typography>
                 </TableCell>
                 <TableCell sx={{ minWidth: 120 }}>
                   <Typography 
                     variant="body2" 
                     sx={{ 
                       fontFamily: 'monospace', 
                       fontSize: '0.8rem',
                       color: entry.userWallet ? 'text.primary' : 'text.secondary',
                       fontStyle: entry.userWallet ? 'normal' : 'italic'
                     }}
                   >
                     {entry.userWallet ? 
                       `${entry.userWallet.slice(0, 6)}...${entry.userWallet.slice(-4)}` : 
                       'No wallet'
                     }
                   </Typography>
                 </TableCell>
                 <TableCell align="right" sx={{ minWidth: 140 }}>
                   <Typography 
                     variant="body1" 
                     fontWeight="bold"
                     sx={{ 
                       color: 'success.main',
                       fontFamily: 'monospace',
                       fontSize: '0.95rem'
                     }}
                   >
                     {Number(entry.moaniBalance || 0).toLocaleString(undefined, { 
                       maximumFractionDigits: 0 
                     })}
                   </Typography>
                 </TableCell>
                 <TableCell sx={{ minWidth: 110 }}>
                   <Typography 
                     variant="body2" 
                     sx={{ 
                       fontSize: '0.85rem',
                       color: 'text.secondary'
                     }}
                   >
                     {entry.createdAt ? 
                       new Date(entry.createdAt).toLocaleDateString(undefined, { 
                         year: '2-digit', 
                         month: 'short', 
                         day: 'numeric' 
                       }) : 
                       'N/A'
                     }
                   </Typography>
                 </TableCell>
                 <TableCell sx={{ minWidth: 110 }}>
                   <Typography 
                     variant="body2" 
                     sx={{ 
                       fontSize: '0.85rem',
                       color: entry.lastOnline ? 'text.secondary' : 'text.disabled',
                       fontStyle: entry.lastOnline ? 'normal' : 'italic'
                     }}
                   >
                     {entry.lastOnline ? 
                       new Date(entry.lastOnline).toLocaleDateString(undefined, { 
                         year: '2-digit', 
                         month: 'short', 
                         day: 'numeric' 
                       }) : 
                       'Never'
                     }
                   </Typography>
                 </TableCell>
                 <TableCell align="center" sx={{ minWidth: 100 }}>
                   {entry.userId && (
                     <Tooltip title="Refresh NFT Collection">
                       <IconButton
                         size="small"
                         onClick={(event) => handleRefreshNFT(entry.userId, event)}
                         disabled={refreshingUsers.has(entry.userId)}
                         sx={{
                           color: refreshingUsers.has(entry.userId) ? 'text.disabled' : 'primary.main',
                           '&:hover': {
                             backgroundColor: 'action.hover'
                           }
                         }}
                       >
                         {refreshingUsers.has(entry.userId) ? (
                           <CircularProgress size={20} />
                         ) : (
                           <RefreshIcon fontSize="small" />
                         )}
                       </IconButton>
                     </Tooltip>
                   )}
                 </TableCell>
                                </TableRow>
               );
             })}
             {leaderboard.length === 0 && (
               <TableRow>
                 <TableCell colSpan={9} align="center">
                   <Typography color="text.secondary" sx={{ py: 4 }}>
                     No users with moani balance found
                   </Typography>
                 </TableCell>
               </TableRow>
             )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function AuctionHouseLeaderboard() {
  const navigate = useNavigate();
  const { selectUserById } = useUser();
  const [from, setFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(() => (new Date()).toISOString().slice(0, 10));
  const [activeLeaderboard, setActiveLeaderboard] = useState('sales');

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['analytics', 'auction-house-leaderboard', from, to], 
    queryFn: async () => {
      const response = await api.get('/analytics/auction-house-leaderboard', { 
        params: { from, to } 
      });
      return response.data;
    }
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography>Loading auction house leaderboard...</Typography>
        </Stack>
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load auction house leaderboard.
      </Alert>
    );
  }

  const { salesLeaderboard = [], purchasesLeaderboard = [] } = data || {};
  const currentLeaderboard = activeLeaderboard === 'sales' ? salesLeaderboard : purchasesLeaderboard;


  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="h6" gutterBottom>Auction House Leaderboard</Typography>
      
      {/* Date Range Controls */}
      <Stack direction="row" spacing={2} mb={2} alignItems="center">
        <InputLabel>Date Range:</InputLabel>
        <TextField 
          type="date" 
          size="small" 
          value={from} 
          onChange={e => setFrom(e.target.value)} 
        />
        <TextField 
          type="date" 
          size="small" 
          value={to} 
          onChange={e => setTo(e.target.value)} 
        />
        <Button variant="contained" onClick={() => refetch()}>Apply</Button>
      </Stack>

      {/* Leaderboard Type Tabs */}
      <Tabs 
        value={activeLeaderboard} 
        onChange={(_, value) => setActiveLeaderboard(value)}
        sx={{ mb: 3 }}
      >
        <Tab value="sales" label={`Top Sellers (${salesLeaderboard.length})`} />
        <Tab value="purchases" label={`Top Buyers (${purchasesLeaderboard.length})`} />
      </Tabs>

      {/* Leaderboard Table */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Rank</strong></TableCell>
              <TableCell><strong>User</strong></TableCell>
              <TableCell><strong>Wallet</strong></TableCell>
              <TableCell align="right">
                <strong>
                  {activeLeaderboard === 'sales' ? 'Sales Count' : 'Purchases Count'}
                </strong>
              </TableCell>
              <TableCell align="right">
                <strong>
                  {activeLeaderboard === 'sales' ? 'Items Sold' : 'Items Bought'}
                </strong>
              </TableCell>
              <TableCell align="right">
                <strong>
                  {activeLeaderboard === 'sales' ? 'Moani Earned' : 'Moani Spent'}
                </strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentLeaderboard.map((entry, index) => (
              <TableRow key={entry.userId || index} hover>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <Typography 
                      variant="body2" 
                      fontWeight="bold"
                      sx={{
                        color: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : index === 2 ? '#CD7F32' : 'inherit',
                        minWidth: '30px'
                      }}
                    >
                      #{index + 1}
                    </Typography>
                    {index < 3 && (
                      <Box 
                        sx={{ 
                          ml: 1, 
                          fontSize: '16px',
                          filter: index === 0 ? 'sepia(1) saturate(2) hue-rotate(38deg)' : 
                                  index === 1 ? 'sepia(1) saturate(0) brightness(1.2)' : 
                                  'sepia(1) saturate(2) hue-rotate(20deg)'
                        }}
                      >
                        🏆
                      </Box>
                    )}
                  </Box>
                </TableCell>
                                 <TableCell>
                  <Typography 
                    component={entry.userId ? 'a' : 'span'}
                    href={entry.userId ? `/user/${entry.userId}/details` : undefined}
                    variant="body2" 
                    fontWeight="medium"
                    sx={{ 
                      cursor: entry.userId ? 'pointer' : 'default',
                      color: entry.userId ? 'primary.main' : 'inherit',
                      textDecoration: 'none',
                      '&:hover': entry.userId ? {
                        color: 'primary.dark',
                        textDecoration: 'underline'
                      } : {}
                    }}
                    onClick={(event) => entry.userId && selectUserById(entry.userId, navigate)}
                    title={entry.userId ? 'Click to select this user (Ctrl+click to open in new tab)' : 'User ID not available'}
                  >
                    {entry.userName || 'Unknown User'}
                  </Typography>
                 </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {entry.userWallet ? 
                      `${entry.userWallet.slice(0, 6)}...${entry.userWallet.slice(-4)}` : 
                      'N/A'
                    }
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">
                    {activeLeaderboard === 'sales' ? 
                      entry.totalSales?.toLocaleString() : 
                      entry.totalPurchases?.toLocaleString()
                    }
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">
                    {activeLeaderboard === 'sales' ? 
                      entry.totalItemsSold?.toLocaleString() : 
                      entry.totalItemsBought?.toLocaleString()
                    }
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography 
                    variant="body2" 
                    fontWeight="bold"
                    sx={{ 
                      color: activeLeaderboard === 'sales' ? '#4caf50' : '#f44336'
                    }}
                  >
                    {activeLeaderboard === 'sales' ? 
                      Number(entry.totalMoaniEarned || 0).toLocaleString(undefined, { 
                        maximumFractionDigits: 2, 
                        minimumFractionDigits: 2 
                      }) : 
                      Number(entry.totalMoaniSpent || 0).toLocaleString(undefined, { 
                        maximumFractionDigits: 2, 
                        minimumFractionDigits: 2 
                      })
                    }
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {currentLeaderboard.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary" sx={{ py: 4 }}>
                    No {activeLeaderboard === 'sales' ? 'sales' : 'purchase'} data found for the selected time period
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary Stats */}
      {currentLeaderboard.length > 0 && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Summary ({from} to {to})
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                Total {activeLeaderboard === 'sales' ? 'Sellers' : 'Buyers'}
              </Typography>
              <Typography variant="h6">
                {currentLeaderboard.length}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                Total {activeLeaderboard === 'sales' ? 'Sales' : 'Purchases'}
              </Typography>
              <Typography variant="h6">
                {currentLeaderboard.reduce((sum, entry) => 
                  sum + (activeLeaderboard === 'sales' ? entry.totalSales : entry.totalPurchases), 0
                ).toLocaleString()}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                Total Moani {activeLeaderboard === 'sales' ? 'Earned' : 'Spent'}
              </Typography>
              <Typography variant="h6" sx={{ color: activeLeaderboard === 'sales' ? '#4caf50' : '#f44336' }}>
                {currentLeaderboard.reduce((sum, entry) => 
                  sum + Number(activeLeaderboard === 'sales' ? entry.totalMoaniEarned : entry.totalMoaniSpent), 0
                ).toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      )}
    </Paper>
  );
}

export default function Analytics() {
  const [tab, setTab] = useState(0);
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Analytics</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label="Moani Leaderboard" />
        <Tab label="Moani" />
        <Tab label="Moani Overview" />
        <Tab label="Auction" />
        <Tab label="Auction Leaderboard" />
        <Tab label="Inventory Items" />
      </Tabs>
      {tab === 0 && <MoaniLeaderboard />}
      {tab === 1 && <MoaniAnalytics />}
      {tab === 2 && <MoaniOverview />}
      {tab === 3 && <AuctionAnalytics />}
      {tab === 4 && <AuctionHouseLeaderboard />}
      {tab === 5 && <InventoryItemsAnalytics />}
    </Box>
  );
} 